import { useState } from "react";
import { Navigate, useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Logo } from "../components/site/Logo";
import { FormError } from "../components/forms/FormBits";

const RegisterForm = ({ onDone }) => {
  const { setUser } = useAuth();
  const [step, setStep] = useState(1);
  const [f, setF] = useState({ email: "", code: "", name: "", password: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const run = async (fn) => {
    setBusy(true);
    setError("");
    try { await fn(); } catch (err) { setError(formatApiError(err)); } finally { setBusy(false); }
  };
  const send = (e) => { e.preventDefault(); run(async () => { await api.post("/auth/register/start", { email: f.email }); setStep(2); }); };
  const complete = (e) => {
    e.preventDefault();
    if (f.password.length < 10) return setError("Password must be at least 10 characters.");
    if (f.password !== f.confirm) return setError("Passwords do not match.");
    run(async () => {
      const { data } = await api.post("/auth/register/complete", { email: f.email, code: f.code, name: f.name, password: f.password });
      setUser(data);
      onDone();
    });
  };
  const input = (k, label, type = "text", auto) => (
    <div className="space-y-2">
      <label htmlFor={`reg-${k}`} className="text-sm font-semibold">{label}</label>
      <Input id={`reg-${k}`} type={type} autoComplete={auto} value={f[k]} onChange={set(k)} className="h-11 bg-white" data-testid={`admin-register-${k}-input`} required />
    </div>
  );
  return step === 1 ? (
    <form onSubmit={send} className="space-y-5" data-testid="admin-register-form">
      <p className="text-sm text-[#5A6561]">Use your Friendship Pen Care Services business email. We will email you a 6-digit verification code.</p>
      {input("email", "Business email", "email", "username")}
      <FormError message={error} testId="admin-register-error" />
      <button type="submit" disabled={busy} className="btn-pill btn-primary w-full" data-testid="admin-register-send-code-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send verification code"}</button>
    </form>
  ) : (
    <form onSubmit={complete} className="space-y-4" data-testid="admin-register-verify-form">
      <p className="text-sm text-[#5A6561]">Enter the code sent to {f.email} and choose a password (at least 10 characters).</p>
      {input("code", "Verification code", "text", "one-time-code")}
      {input("name", "Your name", "text", "name")}
      {input("password", "Password", "password", "new-password")}
      {input("confirm", "Confirm password", "password", "new-password")}
      <FormError message={error} testId="admin-register-error" />
      <button type="submit" disabled={busy} className="btn-pill btn-primary w-full" data-testid="admin-register-complete-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}</button>
    </form>
  );
};

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next")?.startsWith("/admin") ? params.get("next") : "/admin";
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={next} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      navigate(next);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAF8F5]">
      <div className="hidden lg:flex flex-col justify-between bg-[#1E3A2B] p-12 grain text-[#FAF8F5]">
        <Logo light />
        <div>
          <p className="font-display text-4xl leading-tight max-w-md">Admin Portal</p>
          <p className="text-sm text-[#E5E0D8] mt-4 max-w-sm">Authorized staff only. All access is restricted to approved administrator accounts.</p>
        </div>
        <p className="text-xs text-[#E5E0D8]/70">Friendship Pen Care Services</p>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm space-y-5">
          <div className="lg:hidden mb-6"><Logo /></div>
          <div className="h-11 w-11 rounded-xl bg-[#F3EFEA] grid place-items-center"><Lock className="h-5 w-5 text-[#C86D51]" /></div>
          <h1 className="text-3xl font-semibold">{mode === "login" ? "Admin Portal sign-in" : "Create or reset account"}</h1>
          {mode === "register" ? <RegisterForm onDone={() => navigate(next)} /> : (
        <form onSubmit={onSubmit} className="space-y-5" data-testid="admin-login-form">
          <div className="space-y-2">
            <label htmlFor="admin-email" className="text-sm font-semibold">Email</label>
            <Input id="admin-email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 bg-white" data-testid="admin-login-email-input" required />
          </div>
          <div className="space-y-2">
            <label htmlFor="admin-password" className="text-sm font-semibold">Password</label>
            <Input id="admin-password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 bg-white" data-testid="admin-login-password-input" required />
          </div>
          <FormError message={error} testId="admin-login-error" />
          <button type="submit" disabled={busy} className="btn-pill btn-primary w-full disabled:opacity-60" data-testid="admin-login-submit-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
          </button>
        </form>
          )}
          <button type="button" onClick={() => setMode(mode === "login" ? "register" : "login")} className="block w-full text-center text-sm font-semibold text-[#1E3A2B] underline" data-testid="admin-toggle-register-link">
            {mode === "login" ? "First time here or forgot your password? Create or reset your account" : "Back to sign in"}
          </button>
          <Link to="/" className="block text-center text-sm text-[#5A6561] underline" data-testid="admin-login-back-link">Back to website</Link>
        </div>
      </div>
    </div>
  );
}
